import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Navbar from "../Component/LandingV2/Navbar";
import "./ForgotPasswordPage.scss";

const ForgotPasswordPage = () => {
  // Form state
  const [email, setEmail] = useState("");
  const [loadingState, setLoadingState] = useState(false);
  const [messageState, setMessageState] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);

  const navigate = useNavigate();

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic email validation
    if (!validateEmail(email)) {
      setErrorState("Please enter a valid email address.");
      return;
    }

    setLoadingState(true);
    setErrorState(null); // Clear previous error
    setMessageState(null); // Clear previous message

    try {
      // Sending POST request to the backend '/forgot-password' endpoint
      const res = await fetch(
        `${
          process.env.REACT_APP_AUTH_URL || "http://localhost:3000"
        }/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }), // Sending email as JSON
        }
      );

      // Parse the response JSON
      const resData = await res.json();

      // Check if the response was successful
      if (res.ok) {
        // Show success message
        setMessageState(
          "Password reset email sent successfully🎉🎉! Check your inbox,spam or junk folder."
        );
        // Optionally, you can navigate the user to another page after success
        // setTimeout(() => {
        //   navigate("/"); // Redirect to home or login page after success
        // }, 2000);
      } else {
        // If backend responds with an error, throw an error
        throw new Error(resData?.message || "Could not send reset email");
      }
    } catch (err: any) {
      // Handle any errors that occurred during the request
      setErrorState(
        err?.message || "Could not send Reset Password Email. Try Again Later."
      );
    } finally {
      // Reset loading state after the request completes
      setLoadingState(false);
    }
  };

  return (
    <div className="fp-page-root">
      <Navbar />
      <div className="fp-split-page">

        {/* ── LEFT PANEL ── */}
        <div className="fp-left-panel">
          <div className="lp-brand">
            <img src="/assets/images/landingv2/main-logo.png" alt="AlgoFabric" className="lp-brand-logo" />
            <span className="lp-brand-name">AlgoFabric</span>
          </div>
          <div className="lp-center-text">
            <h2 className="lp-center-heading">
              AlgoFabric: A Financial<br />Insights Companion
            </h2>
            <p className="lp-center-body">
              With the power of NLP, AlgoFabric crafts precision stock reports — the
              perfect financial strategy for your journey.
            </p>
          </div>
          <div className="lp-bottom-spacer" />
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="fp-right-panel">
          <div className="fp-form-card">
            <div className="fp-back-link">
              <Link to="/login">← Back to Sign In</Link>
            </div>
            <h2 className="fp-title">Reset Password</h2>
            <p className="fp-subtitle">
              Enter your email and we'll send you a reset link.
            </p>

            <form onSubmit={onSubmit} className="fp-form">
              <div className="form-field">
                <label htmlFor="email" className="field-label">Email Address</label>
                <input
                  id="email"
                  className="input-field"
                  type="text"
                  name="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button type="submit" className="fp-button" disabled={loadingState}>
                {loadingState && (
                  <Icon icon="akar-icons:loading" className="animate-spin" />
                )}
                {loadingState ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            {errorState && (
              <p className="feedback-message feedback-message--error">{errorState}</p>
            )}
            {messageState && (
              <p className="feedback-message feedback-message--success">{messageState}</p>
            )}

            <div className="redirect-login">
              Remembered your password?{" "}
              <Link to="/login">Sign In</Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ForgotPasswordPage;
